import type { SlugFilters } from '@utils/slugGenerator';
import { FILTER_TYPE_PRIORITY, getFilterType } from '@utils/filterTypeUtils';
import { getFilterValues } from '@utils/filtersCached';

export const getFilterTypeKey = (filterType: string): keyof SlugFilters | undefined => {
  const typeMap: Record<string, keyof SlugFilters> = {
    'brand': 'brand',
    'bodyType': 'bodyType',
    'fuelType': 'fuelType',
    'transmission': 'transmission',
    'seatingCapacity': 'seatingCapacity',
    'budget': 'budget'
  };
  
  return typeMap[filterType];
};

export const generateFilterValue = (filterType: string, filterValue: string): string => {
  return filterValue.toLowerCase();
};

export const buildSlugFromFilters = (filters: SlugFilters): string => {
  const slugParts: string[] = [];
  
  FILTER_TYPE_PRIORITY.forEach(filterType => {
    const value = filters[filterType];
    if (value) {
      if (Array.isArray(value)) {
        slugParts.push(...value);
      } else {
        slugParts.push(value);
      }
    }
  });
  
  if (slugParts.length === 0) {
    return 'all-cars';
  }
  
  return `${slugParts.join('+')}+cars/`;
};

export const parseSlugToFilters = (slug: string): SlugFilters => {
  if (!slug || slug === 'all-cars') {
    return {};
  }

  const filterPart = decodeURIComponent(slug)
    .replace(/^\/+|\/+$/g, '')
    .replace(/[+-]cars$/, '');
  const filterValues = filterPart.split('+');

  const filters: SlugFilters = {};

  filterValues.forEach(value => {
    const lowerValue = value.trim().toLowerCase();
    if (!lowerValue) {
      return;
    }

    const filterType = getKnownFilterType(lowerValue);
    if (filterType && filterType !== 'features') {
      filters[filterType] = lowerValue;
    }
  });

  return filters;
};

const getKnownFilterType = (value: string): keyof SlugFilters | undefined => {
  const configuredFilterType = getFilterType(value);
  if (configuredFilterType) {
    return configuredFilterType;
  }

  if (getFilterValues().brandValues.includes(value)) return 'brand';
  if (getFilterValues().bodyTypeValues.includes(value)) return 'bodyType';
  if (getFilterValues().fuelTypeValues.includes(value)) return 'fuelType';
  if (getFilterValues().transmissionValues.includes(value)) return 'transmission';
  if (getFilterValues().budgetValues.includes(value)) return 'budget';
  if (getFilterValues().seatingCapacityValues.includes(value)) return 'seatingCapacity';

  return undefined;
};

export const isLikelyBrand = (value: string): boolean => {
  return getKnownFilterType(value) === 'brand';
};

export const isLikelyBodyType = (value: string): boolean => {
  return getKnownFilterType(value) === 'bodyType';
};

export const isLikelyFuelType = (value: string): boolean => {
  return getKnownFilterType(value) === 'fuelType';
};

export const isLikelyTransmission = (value: string): boolean => {
  return getKnownFilterType(value) === 'transmission';
};

export const isLikelySeatingCapacity = (value: string): boolean => {
  return getKnownFilterType(value) === 'seatingCapacity';
};

export const isLikelyBudget = (value: string): boolean => {
  return getKnownFilterType(value) === 'budget';
};

export const getCurrentFilters = (pathname?: string | null): SlugFilters => {
  const decodedPathname = decodeURIComponent(pathname || '');
  const pathParts = decodedPathname.split('/');
  let currentSlug = 'all-cars';

  if (pathParts && pathParts.length >= 3 && pathParts[1] === 'collection') {
    currentSlug = pathParts[2];
  }

  return parseSlugToFilters(currentSlug);
};
